import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, Users, Pencil, User } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

import { API } from '@/lib/apiBase';

export const PhotoCard = ({ photo, students, onEdit, onDelete, selected, onToggleSelect }) => {
  const { t } = useLanguage();
  const assignedStudents = students.filter((s) => (photo.student_ids || []).includes(s.student_id));

  return (
    <Card
      className={`bg-card border-border overflow-hidden card-hover group cursor-pointer ${selected ? 'ring-2 ring-primary' : ''}`}
      onClick={() => onToggleSelect(photo.photo_id)}
      data-testid={`photo-card-${photo.photo_id}`}
    >
      <div className="aspect-square relative">
        <img
          src={`${API}/photos/${photo.photo_id}/preview?size=thumb`}
          alt={t('common.photoAlt')}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
        {/* Checkbox is now a visual affordance only — the whole card is clickable. */}
        <div
          className={`absolute top-2 left-2 z-10 pointer-events-none transition-opacity ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        >
          <Checkbox
            checked={selected}
            className="bg-card/80 border-2 border-white/80 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            data-testid={`select-photo-${photo.photo_id}`}
            aria-label="Select photo"
            tabIndex={-1}
          />
        </div>
        {photo.is_class_photo && (
          <div className="absolute top-2 left-12 px-2 py-1 bg-primary text-xs font-bold rounded-sm flex items-center gap-1">
            <Users className="w-3 h-3" />{t('admin.classPhoto')}
          </div>
        )}
        {photo.is_teacher_photo && (
          <div className="absolute top-2 left-12 px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded-sm flex items-center gap-1">
            <User className="w-3 h-3" />{t('admin.teacherPhoto')}
          </div>
        )}
        {/* Edit + Delete buttons stop click propagation so they don't also toggle selection. */}
        <div
          className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost" size="icon"
            onClick={() => onEdit(photo)}
            className="bg-black/50 hover:bg-primary h-8 w-8"
            data-testid={`edit-photo-${photo.photo_id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost" size="icon"
            onClick={() => onDelete(photo.photo_id)}
            className="bg-black/50 hover:bg-destructive h-8 w-8"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono text-sm font-bold text-primary">CHF {photo.price?.toFixed(2)}</span>
          <span className="text-xs text-muted-foreground font-mono">{photo.photo_id.slice(-8)}</span>
        </div>
        {assignedStudents.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground truncate">
            <User className="w-3 h-3 shrink-0" />
            <span className="truncate">{assignedStudents.map((s) => s.name).join(', ')}</span>
          </div>
        )}
        {!photo.is_class_photo && assignedStudents.length === 0 && (
          <div className="text-xs text-yellow-500 italic">{t('admin.noStudentAssigned')}</div>
        )}
      </CardContent>
    </Card>
  );
};
